import dayjs from "dayjs";
import { Fragment, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";

import { useEltJobsCompletionStatsQuery } from "@/apollo/types";
import { Button } from "@/components/elements/Button";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import Tooltip from "@/components/elements/Tooltip";
import cn from "@/helpers/classNames";
import { formatPercent, formatRowCount } from "@/shared/formatters";
import { Listbox, Transition } from "@headlessui/react";
import { CalendarDaysIcon } from "@heroicons/react/16/solid";
import { CheckIcon } from "@heroicons/react/20/solid";

const periodOptions = [
  {
    label: "24 Hours",
    value: "1d",
  },
  {
    label: "30 Days",
    value: "30d",
  },
] as const;

type PeriodOption = (typeof periodOptions)[number];

export function EltJobsCompletionStats() {
  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null);
  const [popperElement, setPopperElement] = useState<any>(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    strategy: "absolute",
    placement: "bottom-end",
    modifiers: [
      { name: "preventOverflow", enabled: true },
      { name: "offset", options: { offset: [0, 4] } },
    ],
  });

  const [period, setPeriod] = useState<PeriodOption>(periodOptions[1]);

  const fromDate = useMemo(() => {
    const toDate = (v: (typeof period)["value"]) => {
      switch (v) {
        case "1d":
          return dayjs().subtract(1, "days").toDate();
        default:
          return dayjs().subtract(30, "days").toDate();
      }
    };
    const date = toDate(period.value);
    // Round to the nearest 5 minutes
    const coeff = 1000 * 60 * 5;
    return new Date(Math.floor(date.getTime() / coeff) * coeff);
  }, [period]);

  const { data, loading } = useEltJobsCompletionStatsQuery({
    variables: {
      from: fromDate,
    },
  });
  const stats = data?.eltJobsCompletionStats.stats;
  return (
    <div className="flex divide-x pb-2">
      <div className="max-w-32 px-3 md:px-6 lg:max-w-none xl:px-8">
        <Tooltip
          content={
            <>
              The total count of synchronization jobs completed during the
              period.
            </>
          }
        >
          <div className="mb-1.5 truncate text-xs text-muted-foreground">
            Total sync jobs
          </div>
        </Tooltip>
        <div className="flex h-7 items-center text-base font-semibold md:text-xl lg:text-2xl">
          {loading ? (
            <LoadingSpinner />
          ) : stats ? (
            <span className="truncate leading-none">
              {formatRowCount(stats.total)}
            </span>
          ) : (
            <StatsNotAvailable />
          )}
        </div>
      </div>
      <div className="max-w-32 px-3 md:px-6 lg:max-w-none xl:px-8">
        <Tooltip
          content={
            <>
              The percentage of synchronization jobs that completed successfully
              during the period.
            </>
          }
        >
          <div className="mb-1.5 truncate text-xs text-muted-foreground">
            Success rate
          </div>
        </Tooltip>
        <div className="flex h-7 items-center text-base font-semibold md:text-xl lg:text-2xl">
          {loading ? (
            <LoadingSpinner />
          ) : stats ? (
            <span className="truncate leading-none">
              {formatPercent(stats.successPercent, {
                minimumFractionDigits: 0,
                maximumFractionDigits: stats.successPercent > 0.999 ? 2 : 1,
              })}
            </span>
          ) : (
            <StatsNotAvailable />
          )}
        </div>
      </div>
      <div className="px-3 md:pl-6 xl:pl-8">
        <div className="lg:min-w-24">
          <div className="mb-1.5 text-xs text-muted-foreground">Period</div>
          <Listbox
            value={period}
            onChange={setPeriod}
            as="div"
            className="relative"
            by="value"
          >
            <Listbox.Button
              as={Button}
              size="sm"
              ref={setReferenceElement}
              className="h-7 min-w-7 px-0 lg:px-2"
              icon={<CalendarDaysIcon className="m-0 lg:mr-2" />}
            >
              <span className="hidden lg:inline">
                {periodOptions.find((o) => o.value === period.value)?.label ??
                  "Select period"}
              </span>
            </Listbox.Button>
            {createPortal(
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Listbox.Options
                  ref={(elm) =>
                    setPopperElement(elm as HTMLUListElement | null)
                  }
                  style={styles.popper}
                  {...attributes.popper}
                  className="absolute z-10 max-h-80 w-fit max-w-sm overflow-auto rounded-sm bg-background py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-gray-900"
                >
                  {periodOptions.map((option) => (
                    <Listbox.Option
                      key={option.label}
                      value={option}
                      className={({ active }) =>
                        cn(
                          `relative flex cursor-default select-none items-center gap-2 px-4 py-2`,
                          {
                            "bg-gray-200 dark:bg-gray-700": active,
                          },
                        )
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span>{option.label}</span>
                          <CheckIcon
                            className={cn("ml-auto h-4 w-4 opacity-0", {
                              "opacity-100": selected,
                            })}
                          />
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>,
              document.body,
            )}
          </Listbox>
        </div>
      </div>
    </div>
  );
}

function StatsNotAvailable() {
  return (
    <Tooltip content="Metric not available for this period">
      <div className="text-sm italic text-muted-foreground underline decoration-dashed underline-offset-2">
        N/A
      </div>
    </Tooltip>
  );
}
